import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import React from "react";
import { getIn, useFormikContext } from "formik";
import {
  IssueAttributes,
  IssueCustomAttributeDefinition,
  IssueCustomAttributeMapping,
  IssueCustomAttributeType,
  IssueCustomListAttributeDefinition,
} from "@adsk/acc-issues-types";
import { FieldWrapper } from "@adsk/acc-issues-common/components/primitive/FieldWrapper";
import { getCustomFieldEntitiesMap } from "@adsk/acc-issues-core/state/redux/ducks/issueCustomFields";
import Textarea from "@adsk/alloy-react-textarea";
import TextInput from "@adsk/alloy-react-text-input";
import { IssueCustomListDropdown } from "@adsk/acc-issues-common/components/input/IssueCustomListDropdown";
import { useTranslations } from "@adsk/acc-issues-translations";
import styled from "styled-components";
import { InfoTooltip } from "@adsk/acc-issues-common/components/primitive/InfoTooltip";

const Parent = styled.div`
  display: flex;
  align-items: center;
`;
const Child = styled.div`
  flex: 1;
`;

export const CustomFieldWithFormikContext = ({
  attributeDefinitionId,
  isRequired,
}: IssueCustomAttributeMapping) => {
  const {
    values,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    touched,
    errors,
    submitCount,
  } = useFormikContext<IssueAttributes>();
  const issueCustomFieldEntitiesMap = useSelector(getCustomFieldEntitiesMap);
  const attribute: IssueCustomAttributeDefinition =
    issueCustomFieldEntitiesMap[attributeDefinitionId];
  const { T } = useTranslations();
  if (!attribute) {
    return null;
  }

  const fieldPath = `customAttributes.${attributeDefinitionId}`;
  const errorMessage =
    (getIn(touched, ["customAttributes", attributeDefinitionId]) ||
      submitCount > 0) &&
    getIn(errors, ["customAttributes", attributeDefinitionId]);

  let testId;
  switch (attribute.dataType) {
    case IssueCustomAttributeType.List:
      testId = "cf-list-field";
      break;
    case IssueCustomAttributeType.Text:
      testId = "cf-text-field";
      break;
    case IssueCustomAttributeType.Paragraph:
      testId = "cf-paragraph-field";
      break;
    case IssueCustomAttributeType.Numeric:
      testId = "cf-numeric-field";
      break;
  }

  const displayableLabel = !attribute.description ? (
    attribute.title
  ) : (
    <Parent>
      <Child style={{ whiteSpace: "nowrap" }}>{attribute.title}</Child>
      <Child>
        <InfoTooltip content={attribute.description} />
      </Child>
    </Parent>
  );

  // When we pass the children from FieldWrapper to FormField here, we use an object and apply [customField.dataType] to it.
  // We don't use a function with switch/case because the function wouldn't end up returning the component
  return (
    <FieldWrapper
      label={displayableLabel}
      errorMessage={errorMessage}
      required={isRequired}
      testId={testId}
    >
      {
        {
          [IssueCustomAttributeType.List]: (
            <IssueCustomListDropdown
              error={!!errorMessage}
              attribute={attribute as IssueCustomListAttributeDefinition}
              onChange={value => {
                /**
                 * For whatever reason, setting the value via the field path
                 * does not let that value get immediately used for validation.
                 *
                 * So, either we do something like:
                 * ```
                 * setFieldValue("customAttributes", {
                 *   ...values.customAttributes,
                 *   [attributeDefinitionId]: value,
                 * });
                 * ```
                 *
                 * or we use setTimeout to defer the validation until after the fieldPath modification
                 * gets taken into account for validation.
                 *
                 * Without this, the first time the user changes a custom attribute, it will say "required"
                 * event though they just changed it.
                 *
                 * We're taking the latter approach for now to stay consistent with the use of fieldPath
                 * elsewhere in this module.
                 *
                 */
                setFieldValue(fieldPath, value, /* shouldValidate: */ false);
                window.setTimeout(() => {
                  setFieldTouched(fieldPath, true);
                }, 0);
              }}
              required={isRequired}
              value={getIn(values, fieldPath)}
            />
          ),
          [IssueCustomAttributeType.Text]: (
            <TextInput
              placeholder={
                T.translate("customFieldsAdmin.placeholder.text") as string
              }
              name={fieldPath}
              autoComplete="off"
              value={getIn(values, fieldPath)}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errorMessage}
            />
          ),
          [IssueCustomAttributeType.Numeric]: (
            <TextInput
              placeholder={
                T.translate("customFieldsAdmin.placeholder.numeric") as string
              }
              name={fieldPath}
              autoComplete="off"
              value={getIn(values, fieldPath)}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errorMessage}
            />
          ),
          [IssueCustomAttributeType.Paragraph]: (
            <Textarea
              style={{ width: "100%" }}
              placeholder={
                T.translate("customFieldsAdmin.placeholder.text") as string
              }
              // @ts-ignore https://jira.autodesk.com/browse/DSGN-661
              name={fieldPath}
              autoComplete="off"
              value={getIn(values, fieldPath)}
              onChange={handleChange}
              onBlur={handleBlur}
              minHeight={70}
              maxHeight={70}
              error={!!errorMessage}
            />
          ),
          [IssueCustomAttributeType.Date]: null,
          [IssueCustomAttributeType.Checkbox]: null,
        }[attribute.dataType]
      }
    </FieldWrapper>
  );
};
